// @owners { team: patients-team }
import { useNavigation } from '@alto/navigation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { completeOnboarding } from '~shared/actions/auth';
import { openModal } from '~shared/actions/modal';
import { clearOnboardingState } from '~shared/actions/onboarding';
// eslint-disable-next-line import/no-deprecated
import { fetchAppDownloadLink } from '~shared/actions/users';
import { ONBOARD_SUCCESS } from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import { getTempPassword } from '~shared/features/onboarding/selectors/getOnboarding';
import getLoading from '~shared/features/ui/selectors/getLoading';
import getShowProgynySelfServiceForUser from '~shared/features/users/selectors/getShowProgynySelfServiceForUser';
import { isMobileWeb } from '~shared/helpers/helper';
import { useAnalytics, useIsFeatureDisabledForCompany } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { OnboardingProviderSuccess } from './OnboardingProviderSuccess';
import { useNavigationContext } from '~web/contexts';
import { nextUrl } from '~web/features/onboarding/helpers/urlHandler';

const OnboardingProviderSuccessContainer = () => {
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const tempPassword = useSelectorShared(getTempPassword);
  const showProgyny = useSelectorShared(getShowProgynySelfServiceForUser);
  const nextRoute = nextUrl({ currentRoute: ONBOARD_SUCCESS });
  const { fetchAppDownloadLinkLoading } = useSelectorShared(getLoading);
  const { navigate } = useNavigation();
  const { previous: previousWebUrl } = useNavigationContext();
  const origin = routeFromPath(previousWebUrl ?? '');
  const [appDownloadUrl, setAppDownloadUrl] = useState('');
  // need to use tempPasswordRef so that clearing onboarding state (i.e. resetting tempPassword)
  // does not trigger a rerender and cause onContinue to update
  const tempPasswordRef = useRef(tempPassword);
  const { isFeatureDisabled: isDownloadAppDisabledForCompany, isLoading: isFeatureDisablingLoading } =
    useIsFeatureDisabledForCompany('download_app');
  const hasDispatchedOnboardingCompletionRef = useRef(false);
  const hasScheduledDownloadAppModalRef = useRef(false);

  const onContinue = useCallback(() => {
    trackEvent({
      event: EVENTS.PROGYNY_UPSELL__DOWNLOAD_APP_TAPPED,
    });
    if (appDownloadUrl) {
      window.location.assign(appDownloadUrl);
    }
  }, [trackEvent, appDownloadUrl]);

  useEffect(() => {
    if (showProgyny) {
      const fetchAppUrl = async () => {
        // eslint-disable-next-line import/no-deprecated
        const url = await dispatch(fetchAppDownloadLink(tempPasswordRef.current ?? ''));
        setAppDownloadUrl(url);
      };
      fetchAppUrl();
    }
  }, [dispatch, showProgyny]);

  useEffect(() => {
    if (hasDispatchedOnboardingCompletionRef.current) {
      return;
    }
    hasDispatchedOnboardingCompletionRef.current = true;

    // For progyny patients, the final step is the download app(OnboardingProviderSuccess) page
    if (!showProgyny) {
      navigate(nextRoute);
    }
    dispatch(clearOnboardingState());
    dispatch(completeOnboarding());
  }, [dispatch, navigate, nextRoute, showProgyny]);

  useEffect(() => {
    if (hasScheduledDownloadAppModalRef.current) {
      return;
    }
    if (showProgyny || !isMobileWeb() || isDownloadAppDisabledForCompany || isFeatureDisablingLoading) {
      return;
    }

    hasScheduledDownloadAppModalRef.current = true;
    setTimeout(() => {
      dispatch(openModal('GET_THE_APP_DRAWER', { tempPassword: tempPasswordRef.current }));
    }, 300);
  }, [dispatch, showProgyny, isDownloadAppDisabledForCompany, isFeatureDisablingLoading]);

  return (
    showProgyny && (
      <OnboardingProviderSuccess
        onContinue={onContinue}
        showProgyny={showProgyny}
        fetchAppDownloadLinkLoading={fetchAppDownloadLinkLoading}
        origin={origin}
      />
    )
  );
};

export default OnboardingProviderSuccessContainer;
